import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ObjectBaseReportRowType } from '@econtractor/types';
import * as apiReport from 'api/apiReports';
import { useToast } from 'context/ToastContext';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import { canViewSales } from 'utils/helpers/access/hasUserAccess';
import ESpinner from 'components/others/Spinner';
import {
    loadReportObjectsFilters,
    saveReportObjectsFilters,
    type ReportDateFilterState,
} from '../helper/reportObjectsStorage';
import ReportDate from '../ReportDate';
import ReportExportButton from '../ReportExportButton';
import ReportObjectsTable from './ReportObjectsTable';
import VatFilter from './VatFilter';
import type { ReportVatFilter, TypeFilterChange } from '../types';
import { ObjectDetailsOffcanvas } from 'modules/objects';
import useReportObjectsColumns from './model/useReportObjectsColumns';
import { exportObjectsReport } from './model/exportObjectsReport';

const ReportObjects = () => {
    const { t } = useTranslation(['Reports', 'Objects', 'common']);
    const { showToast } = useToast();
    const showToastRef = useRef(showToast);
    showToastRef.current = showToast;
    const { auth } = useAuth();
    const { localSettings } = useSettings();
    const { showMaterials, showServices, showOther } = useReportObjectsColumns();
    const showSales = canViewSales(auth?.user);
    const [exporting, setExporting] = useState(false);
    const savedFilters = useMemo(() => loadReportObjectsFilters(), []);

    const [objectsList, setObjectsList] = useState<ObjectBaseReportRowType[] | null>(null);
    const [isReporting, setIsReporting] = useState(false);
    const [specificationObject, setSpecificationObject] = useState<ObjectBaseReportRowType | null>(null);
    const [dates, setDates] = useState<[number, number] | null>(savedFilters.dates);
    const [vat, setVat] = useState<ReportVatFilter>(savedFilters.vat);
    const [includeObjects, setIncludeObjects] = useState(savedFilters.includeObjects);
    const [includeSales, setIncludeSales] = useState(savedFilters.includeSales);
    const [includeServiceSales, setIncludeServiceSales] = useState(savedFilters.includeServiceSales);
    const [dateFilter, setDateFilter] = useState<ReportDateFilterState>({
        period: savedFilters.period,
        dates: savedFilters.dates,
        customRange: savedFilters.customRange,
    });

    const persistFilters = useCallback(
        (patch: Partial<{
            dates: [number, number] | null;
            vat: ReportVatFilter;
            dateFilter: ReportDateFilterState;
            includeObjects: boolean;
            includeSales: boolean;
            includeServiceSales: boolean;
        }>) => {
            const nextDates = patch.dates !== undefined ? patch.dates : dates;
            const nextVat = patch.vat ?? vat;
            const nextDateFilter = patch.dateFilter ?? dateFilter;
            const nextIncludeObjects = patch.includeObjects ?? includeObjects;
            const nextIncludeSales = patch.includeSales ?? includeSales;
            const nextIncludeServiceSales = patch.includeServiceSales ?? includeServiceSales;

            if (!nextDates?.[0] || !nextDates?.[1]) return;

            saveReportObjectsFilters({
                period: nextDateFilter.period,
                dates: [nextDates[0], nextDates[1]],
                customRange: nextDateFilter.customRange,
                vat: nextVat,
                includeObjects: nextIncludeObjects,
                includeSales: nextIncludeSales,
                includeServiceSales: nextIncludeServiceSales,
            });
        },
        [dates, vat, dateFilter, includeObjects, includeSales, includeServiceSales],
    );

    const handleDateFilterChange = (filter: ReportDateFilterState) => {
        setDateFilter(filter);
        setDates(filter.dates);
        persistFilters({ dates: filter.dates, dateFilter: filter });
    };

    const handleVatChange: Dispatch<SetStateAction<ReportVatFilter>> = (value) => {
        setVat((prev) => {
            const nextVat = typeof value === 'function' ? value(prev) : value;
            persistFilters({ vat: nextVat });
            return nextVat;
        });
    };

    const handleTypeFilterChange = (next: TypeFilterChange) => {
        const nextIncludeObjects = next.includeObjects ?? includeObjects;
        const nextIncludeSales = next.includeSales ?? includeSales;
        const nextIncludeServiceSales = next.includeServiceSales ?? includeServiceSales;
        if (next.includeObjects !== undefined) setIncludeObjects(nextIncludeObjects);
        if (next.includeSales !== undefined) setIncludeSales(nextIncludeSales);
        if (next.includeServiceSales !== undefined) setIncludeServiceSales(nextIncludeServiceSales);
        persistFilters({
            includeObjects: nextIncludeObjects,
            includeSales: nextIncludeSales,
            includeServiceSales: nextIncludeServiceSales,
        });
    };

    const dateFrom = dates?.[0];
    const dateTo = dates?.[1];

    const fetchReport = useCallback(async () => {
        if (!dateFrom || !dateTo) return;
        const salesOn = showSales && includeSales;
        const serviceSalesOn = showSales && includeServiceSales;
        setIsReporting(true);
        setObjectsList(null);
        if (!includeObjects && !salesOn && !serviceSalesOn) {
            setObjectsList([]);
            setIsReporting(false);
            return;
        }
        const resRep = await apiReport.get4Objects(
            dateFrom,
            dateTo,
            vat,
            includeObjects,
            salesOn,
            serviceSalesOn,
        );
        if (!resRep) showToastRef.current(t('common:messError'), 'danger');
        setObjectsList(resRep?.objects ?? null);
        setIsReporting(false);
    }, [dateFrom, dateTo, vat, includeObjects, includeSales, includeServiceSales, showSales, t]);

    useEffect(() => {
        void fetchReport();
    }, [fetchReport]);

    const handleExport = async () => {
        if (!objectsList || exporting) return;
        setExporting(true);
        try {
            const exported = await exportObjectsReport({
                objects: objectsList,
                dates,
                period: dateFilter.period,
                vat,
                includeObjects,
                includeSales,
                includeServiceSales,
                showSales,
                showMaterials,
                showServices,
                showOther,
                rate: Number(localSettings?.RATE?.current ?? 1) || 1,
                t,
            });
            if (!exported) showToast(t('export.empty'), 'warning');
        } catch {
            showToast(t('common:messError'), 'danger');
        } finally {
            setExporting(false);
        }
    };

    return (
        <Row>
            <Col className="col-sticky" sm={12}>
                <div className="w-100 d-sm-flex pt-1 justify-content-between align-items-start gap-2">
                    <div className="flex-shrink-0">
                        <h4 className="p-0 m-0 mb-0 me-2 text-nowrap">
                            {t('common:SideBarUl.itemRepostsObjects')}
                        </h4>
                    </div>
                    <div className="mt-2 mt-sm-0 mb-2 page-toolbar-actions">
                        <ReportExportButton
                            disabled={!objectsList || isReporting || exporting}
                            onClick={() => {
                                void handleExport();
                            }}
                        />
                        <VatFilter
                            vat={vat}
                            setVat={handleVatChange}
                            includeObjects={includeObjects}
                            includeSales={includeSales}
                            includeServiceSales={includeServiceSales}
                            showSales={showSales}
                            onTypeChange={handleTypeFilterChange}
                        />
                        <ReportDate
                            setDates={setDates}
                            disabled={isReporting}
                            initialFilter={dateFilter}
                            onFilterChange={handleDateFilterChange}
                        />
                    </div>
                </div>
            </Col>

            <Col sm={12}>
                <div className="px-0">
                    {objectsList ? (
                        <ReportObjectsTable
                            objectsList={objectsList}
                            onShowSpecification={setSpecificationObject}
                        />
                    ) : (
                        <ESpinner />
                    )}
                </div>
            </Col>

            <ObjectDetailsOffcanvas
                show={!!specificationObject}
                onHide={() => setSpecificationObject(null)}
                vobject={specificationObject}
            />
        </Row>
    );
};

export default ReportObjects;
